import { useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { Tooltip } from '@mui/material';
import {
  IInvoiceQuery,
  InvoiceQueryReasonLabels,
  InvoiceQueryResolution,
  InvoiceQueryResolutionLabels,
  InvoiceQuerySource,
  InvoiceQueryStatus,
  IOpenInvoiceQueryRequest,
} from 'shared';

import { Button } from '../../../../common/Atoms/Button';
import { DatePicker } from '../../../../common/Atoms/DatePicker';
import { Modal } from '../../../../common/Atoms/Modal';
import { Select } from '../../../../common/Atoms/Select';
import { TextArea } from '../../../../common/Atoms/TextArea';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { DatePopover } from '../../../../common/Components/DatePopover';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import {
  useFetchInvoiceQueriesQuery,
  useOpenInvoiceQueryMutation,
  useResolveInvoiceQueryMutation,
  useUpdateInvoiceQueryMutation,
} from '../../../services/api/invoiceApi/invoice';
import { InvoiceQueryForm } from '../../Common/InvoiceQueryForm';
import { FromCall } from '../../Molecules/CallTranscript';
import { DetailField } from '../InvoiceV2/DetailField';

const sourceLabel: Record<InvoiceQuerySource, string> = {
  [InvoiceQuerySource.USER]: `recorded in app`,
  [InvoiceQuerySource.PORTAL]: `submitted in portal`,
  [InvoiceQuerySource.AI_AGENT]: `from the AI agent`,
  [InvoiceQuerySource.ESCALATION]: `from an escalation`,
  [InvoiceQuerySource.CALL]: `from a call`,
};

const resolutionOptions = Object.values(InvoiceQueryResolution).map(value => ({
  label: InvoiceQueryResolutionLabels[value],
  value,
}));

const DETAIL_MAX_LENGTH = 60;

function truncate(text: string) {
  return text.length > DETAIL_MAX_LENGTH ? `${text.slice(0, DETAIL_MAX_LENGTH)}...` : text;
}

interface EditQueryFormProps {
  query: IInvoiceQuery;
  onSubmit: (data: { nextAction: string | null; dueDate: string | null }) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

function EditQueryForm({
  query,
  onSubmit,
  onCancel,
  loading,
}: EditQueryFormProps) {
  const [nextAction, setNextAction] = useState<string>(query.nextAction || ``);
  const [dueDate, setDueDate] = useState<string>(query.dueDate || ``);

  return (
    <div className={ `space-y-3` }>
      <TextArea
        label={ `Next action` }
        name={ `nextAction` }
        value={ nextAction }
        rows={ 2 }
        onChange={ ({ value }) => setNextAction(String(value)) }
      />
      <DatePicker
        label={ `Due date` }
        name={ `dueDate` }
        value={ dueDate || null }
        onChange={ ({ value }) => setDueDate(value || ``) }
      />
      <div className={ `flex justify-end gap-2` }>
        <Button color={ `none` }
          onClick={ onCancel }
          disabled={ loading }>
          { `Cancel` }
        </Button>
        <Button onClick={ () => onSubmit({ nextAction: nextAction.trim() || null, dueDate: dueDate || null }) }
          loading={ loading }>
          { `Save` }
        </Button>
      </div>
    </div>
  );
}

interface InvoiceQueryProps {
  unpaid: boolean;
  /** Short summary for the invoice details card */
  compact?: boolean;
}

export function InvoiceQuery({ unpaid, compact }: InvoiceQueryProps) {
  const { organisationId, invoiceId } = useParams();
  const organisation = useGetSelectedOrganisation();
  const { data: queries } = useFetchInvoiceQueriesQuery({ organisationId, invoiceId }, { skip: !organisationId || !invoiceId });
  const [openQuery, { isLoading: opening }] = useOpenInvoiceQueryMutation();
  const [updateQuery, { isLoading: updating }] = useUpdateInvoiceQueryMutation();
  const [resolveQuery, { isLoading: resolving }] = useResolveInvoiceQueryMutation();
  const [openModal, setOpenModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [resolveModal, setResolveModal] = useState(false);
  const [resolution, setResolution] = useState<InvoiceQueryResolution | null>(null);

  const current = queries?.find(d => d.status === InvoiceQueryStatus.OPEN);
  const today = DateTime.now().setZone(organisation.validatedTimezone).toISODate();
  const overdue = !!current?.dueDate && current.dueDate < today;

  async function onOpen(query: IOpenInvoiceQueryRequest) {
    try {
      await openQuery({ organisationId, invoiceId, ...query }).unwrap();
      toast.success(`Query opened`);
      setOpenModal(false);
    }
    catch (err) {
      toast.error(err?.data?.message || err?.data || `Could not open the query`);
    }
  }

  async function onEdit(data: { nextAction: string | null; dueDate: string | null }) {
    if (!current) return;

    try {
      await updateQuery({
        organisationId,
        invoiceId,
        queryId: current.id,
        ...data,
      }).unwrap();

      toast.success(`Query updated`);
      setEditModal(false);
    }
    catch (err) {
      toast.error(err?.data?.message || err?.data || `Could not update the query`);
    }
  }

  async function onResolve() {
    if (!current || !resolution) return;

    try {
      await resolveQuery({
        organisationId,
        invoiceId,
        queryId: current.id,
        resolution,
      }).unwrap();

      toast.success(`Query resolved`);
      setResolveModal(false);
      setResolution(null);
    }
    catch (err) {
      toast.error(err?.data?.message || err?.data || `Could not resolve the query`);
    }
  }

  const modals = (
    <>
      <Modal
        open={ openModal }
        setOpen={ setOpenModal }
        title={ `Open a query` }
      >
        <InvoiceQueryForm
          onSubmit={ onOpen }
          onCancel={ () => setOpenModal(false) }
          loading={ opening }
        />
      </Modal>

      <Modal
        open={ editModal }
        setOpen={ setEditModal }
        title={ `Edit query` }
      >
        { current ? (
          <EditQueryForm
            query={ current }
            onSubmit={ onEdit }
            onCancel={ () => setEditModal(false) }
            loading={ updating }
          />
        ) : null }
      </Modal>

      <Modal
        open={ resolveModal }
        setOpen={ setResolveModal }
        title={ `Resolve query` }
      >
        <div className={ `space-y-3` }>
          <Select
            label={ `Resolution` }
            name={ `resolution` }
            options={ resolutionOptions }
            selected={ resolution }
            onChange={ ({ value }) => setResolution(value) }
          />
          <div className={ `flex justify-end gap-2` }>
            <Button color={ `none` }
              onClick={ () => setResolveModal(false) }
              disabled={ resolving }>
              { `Cancel` }
            </Button>
            <Button onClick={ onResolve }
              loading={ resolving }
              disabled={ !resolution || resolving }>
              { `Resolve query` }
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );

  if (compact) {
    const tooltip = current ? [
      current.detail,
      current.nextAction ? `Next: ${current.nextAction}` : null,
      current.dueDate ? `Due ${current.dueDate}` : null,
    ].filter(Boolean).join(`\n`) : ``;

    return (
      <DetailField label={ `Query` }>
        { current ? (
          <span>
            <Tooltip title={ tooltip ? <span className={ `whitespace-pre-line` }>{ tooltip }</span> : `` }>
              <span className={ overdue ? `text-red-600` : `text-orange-700` }>
                { InvoiceQueryReasonLabels[current.reason] || current.reason }
              </span>
            </Tooltip>
            { ` · ` }
            <button
              type={ `button` }
              onClick={ () => setEditModal(true) }
              className={ `cursor-pointer text-paidniceGreen-600 hover:underline` }
            >
              { `Edit` }
            </button>
            { ` · ` }
            <button
              type={ `button` }
              onClick={ () => setResolveModal(true) }
              className={ `cursor-pointer text-paidniceGreen-600 hover:underline` }
            >
              { `Resolve` }
            </button>
            { current.contactCallId && organisationId ? (
              <FromCall
                organisationId={ organisationId }
                contactId={ current.contactId }
                contactCallId={ current.contactCallId }
              />
            ) : null }
          </span>
        ) : (
          <button
            type={ `button` }
            onClick={ () => setOpenModal(true) }
            disabled={ !unpaid }
            className={ `cursor-pointer text-paidniceGreen-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline` }
          >
            { unpaid ? `Open query` : `Not available` }
          </button>
        ) }
        { modals }
      </DetailField>
    );
  }

  return (
    <div className={ `flex justify-between pt-2` }>
      <Paragraph>
        { `Query` }
      </Paragraph>
      { current ? (
        <div className={ `flex flex-col items-end gap-1` }>
          <Paragraph variant={ `secondary` }>
            { InvoiceQueryReasonLabels[current.reason] || current.reason }
            { ` (${sourceLabel[current.source] || current.source})` }
          </Paragraph>
          { current.contactCallId && organisationId ? (
            <FromCall
              organisationId={ organisationId }
              contactId={ current.contactId }
              contactCallId={ current.contactCallId }
            />
          ) : null }
          { current.detail ? (
            <Tooltip title={ current.detail }>
              <span className={ `text-sm text-slate-500` }>
                { truncate(current.detail) }
              </span>
            </Tooltip>
          ) : null }
          { current.nextAction ? (
            <span className={ `text-sm text-slate-500` }>
              { `Next: ${current.nextAction}` }
            </span>
          ) : null }
          { current.dueDate ? (
            <span className={ `text-sm ${overdue ? `text-red-600` : `text-slate-500`}` }>
              { `Due ` }
              <DatePopover
                date={ current.dueDate }
                labelFormat={ `ccc dd LLL yyyy` }
                organisationTimezone={ organisation.validatedTimezone }
              />
            </span>
          ) : null }
          <div className={ `flex items-center gap-2` }>
            <Button color={ `none` }
              onClick={ () => setEditModal(true) }>
              { `Edit` }
            </Button>
            <Button color={ `none` }
              onClick={ () => setResolveModal(true) }>
              { `Resolve` }
            </Button>
          </div>
        </div>
      ) : (
        <Button color={ `none` }
          onClick={ () => setOpenModal(true) }
          disabled={ !unpaid }>
          { unpaid ? `Open query` : `Not available` }
        </Button>
      ) }

      { modals }
    </div>
  );
}
